import type { userGroupsMapping } from '../indexMappings/userGroups'
import { buildUrl } from '../utils/build-url'
import { fetchThrowsIfNotOk } from '../utils/fetch-throws-if-not-ok'
import type {
  SearchObjectFieldsTypeFromMappingObjectType,
  SearchResponse,
  SearchResult,
} from './types'

type SearchUserGroupsFields = SearchObjectFieldsTypeFromMappingObjectType<
  typeof userGroupsMapping
>

export type SearchUserGroupsRequiredFields = {
  [K in keyof Pick<
    SearchUserGroupsFields,
    'name' | 'userGroupId' | 'description' | 'membersCount'
  >]: SearchUserGroupsFields[K]
}

// Only used to have type safety and SEARCH_NOTE_REQUIRED_FIELDS matching SearchRequiredFields type definition
const searchUserGroupsRequiredFieldsHelper: {
  [K in keyof SearchUserGroupsRequiredFields]: true
} = {
  description: true,
  membersCount: true,
  name: true,
  userGroupId: true,
}

export const SEARCH_USER_GROUPS_REQUIRED_FIELDS = Object.freeze(
  Object.keys(searchUserGroupsRequiredFieldsHelper)
)

export type SearchUserGroupsResult =
  SearchResult<SearchUserGroupsRequiredFields>

export type SearchUserGroupsResponse = SearchResponse<SearchUserGroupsResult>

export type SearchUserGroupsOptionalFilters = {
  excludeUserGroupIdList?: string[]
}

export type SearchUserGroupsQuery = {
  query: string
  optionalFilters?: SearchUserGroupsOptionalFilters
  hitsPerPage: number
  page: number
  explain?: boolean
  profile?: boolean
}

export type SearchUserGroupsHit = {
  id: string
  name: string
  description: string
  membersCount: number
}

export async function fetchUserGroups({
  baseUrl,
  token,
  query,
  hitsPerPage,
  optionalFilters,
  page,
}: {
  baseUrl: string
  token: string
} & SearchUserGroupsQuery): Promise<
  Pick<SearchUserGroupsResponse, 'nbPages' | 'page'> & {
    hits: SearchUserGroupsHit[]
  }
> {
  const response = await fetchThrowsIfNotOk(buildUrl(baseUrl, '/user-groups'), {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    method: 'post',
    body: JSON.stringify({
      optionalFilters,
      query,
      hitsPerPage,
      page,
    }),
  })

  const result: SearchUserGroupsResponse = await response.json()

  const hits = result.hits.map((hit) => ({
    id: hit.fields?.userGroupId?.[0] ?? '',
    name: hit.fields?.name?.[0] ?? '',
    description: hit.fields?.description?.[0] ?? '',
    membersCount: hit.fields?.membersCount?.[0] ?? 0,
  }))

  return {
    nbPages: result.nbPages,
    page: result.page,
    hits,
  }
}
